/**
 * Who worked on a game, and what they did on it.
 *
 * A credit is the join between a person at the studio and a title on the slate, and it carries
 * the two things that make it worth reading: the roles they held on that game, and a line about
 * what that actually meant. The same person is a different credit on every game — level design
 * on one, direction and gameplay code on another — which is exactly why the roles live on the
 * credit and not on the person.
 *
 * The role list is a table for the reason the feature and language lists are tables
 * (`gameDetails.ts`): the admin picker labels itself from it, the API validates against it, and
 * both the studio page and the game page draw from it, so a new discipline is a row here and
 * nothing else anywhere.
 *
 * This file imports nothing but `common.js` on purpose. The game page carries its credits and
 * the studio page carries its people, so the two would otherwise import each other — the parts
 * both sides need are here, and `team.ts` builds the person's side on top.
 */

import { z } from 'zod';
import { slugSchema, timestampSchema, ulidSchema } from './common.js';

/* ------------------------------------------------------------------ roles */

/**
 * How a role is grouped in the picker.
 *
 * Fifteen roles in one flat list is a list nobody reads to the end of. Four groups is the
 * number of departments a game studio actually has, and each one is a question with an obvious
 * answer — did they decide what it is, build it, draw it, or run the studio around it.
 */
export const CREDIT_ROLE_GROUPS = ['design', 'engineering', 'art', 'studio'] as const;
export type CreditRoleGroup = (typeof CREDIT_ROLE_GROUPS)[number];

export const CREDIT_ROLE_GROUP_LABELS: Record<CreditRoleGroup, string> = {
  design: 'Design',
  engineering: 'Engineering',
  art: 'Art and audio',
  studio: 'Studio',
};

export interface CreditRoleSpec {
  id: CreditRoleId;
  /** What goes on the card. Title case, because it is a job and not a sentence. */
  label: string;
  group: CreditRoleGroup;
  /** The line under it in the admin picker, where two neighbouring roles look alike. */
  hint: string;
}

/**
 * Every role a credit can hold, in the order the picker draws them.
 *
 * Deliberately the roles a small studio has rather than the hundred a end-credits roll has.
 * Every one of these is a thing somebody here has actually done on a shipped build; there is no
 * "Executive Producer" because there is nobody to put in it, and a role nobody can be given is
 * a role that makes the list longer and the page less true.
 */
export const CREDIT_ROLES = [
  {
    id: 'direction',
    label: 'Direction',
    group: 'design',
    hint: 'Decided what the game is and held it to that.',
  },
  {
    id: 'game_design',
    label: 'Game design',
    group: 'design',
    hint: 'The rules and the systems: what the player can do and what it costs them.',
  },
  {
    id: 'level_design',
    label: 'Level design',
    group: 'design',
    hint: 'The spaces themselves, blockout through to final pass.',
  },
  {
    id: 'narrative',
    label: 'Writing',
    group: 'design',
    hint: 'Story, script and the words in the game.',
  },
  {
    id: 'programming',
    label: 'Gameplay code',
    group: 'engineering',
    hint: 'The code the player is touching when they play it.',
  },
  {
    id: 'engine',
    label: 'Engine and tools',
    group: 'engineering',
    hint: 'What the rest of the team builds the game with.',
  },
  {
    id: 'ui',
    label: 'UI and UX',
    group: 'engineering',
    hint: 'Menus, HUD, and how a controller gets around all of it.',
  },
  {
    id: 'release',
    label: 'Release',
    group: 'engineering',
    hint: 'Builds, patches and the pipeline that puts one on a player’s machine.',
  },
  {
    id: 'art',
    label: 'Art',
    group: 'art',
    hint: 'Concept, environment, character, props — anything drawn or modelled.',
  },
  {
    id: 'animation',
    label: 'Animation',
    group: 'art',
    hint: 'Everything that moves because somebody made it move.',
  },
  {
    id: 'audio',
    label: 'Audio',
    group: 'art',
    hint: 'Sound design and the mix.',
  },
  {
    id: 'music',
    label: 'Music',
    group: 'art',
    hint: 'The score.',
  },
  {
    id: 'production',
    label: 'Production',
    group: 'studio',
    hint: 'Scope, schedule, and who is on what this week.',
  },
  {
    id: 'qa',
    label: 'QA',
    group: 'studio',
    hint: 'Played it on purpose until it broke, and wrote down how.',
  },
  {
    id: 'community',
    label: 'Community',
    group: 'studio',
    hint: 'The Discord, the build notes, and answering for what shipped.',
  },
] as const satisfies readonly { id: string; label: string; group: CreditRoleGroup; hint: string }[];

export type CreditRoleId = (typeof CREDIT_ROLES)[number]['id'];

export const CREDIT_ROLE_IDS = CREDIT_ROLES.map((r) => r.id) as readonly CreditRoleId[];

export const creditRoleIdSchema = z.enum(
  CREDIT_ROLE_IDS as unknown as [CreditRoleId, ...CreditRoleId[]],
);

export const ROLE_BY_ID: Record<CreditRoleId, CreditRoleSpec> = Object.fromEntries(
  CREDIT_ROLES.map((role) => [role.id, role]),
) as Record<CreditRoleId, CreditRoleSpec>;

/** A credit with fifteen roles on it has said nothing. Four is a person; this is the ceiling. */
export const MAX_CREDIT_ROLES = 6;

/**
 * The roles on one credit, always stored in table order.
 *
 * Sorted here rather than at the point of drawing, so the chips on the studio card and the
 * chips on the game page are in the same order as the checkboxes that set them, and two credits
 * with the same roles look the same.
 */
export const creditRolesSchema = z
  .array(creditRoleIdSchema)
  .max(MAX_CREDIT_ROLES)
  .transform((ids) => {
    const unique = new Set(ids);
    return CREDIT_ROLE_IDS.filter((id) => unique.has(id));
  });

/** Reads a stored `roles` column back, dropping anything the table no longer knows. */
export function parseCreditRoles(raw: string | null | undefined): CreditRoleId[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const ids = new Set(parsed.filter((v): v is string => typeof v === 'string'));
  return CREDIT_ROLE_IDS.filter((id) => ids.has(id));
}

/** The roles of one credit, as words, in table order. For a line rather than a row of chips. */
export function roleLabels(roles: readonly CreditRoleId[]): string[] {
  return roles.map((id) => ROLE_BY_ID[id].label);
}

/* ----------------------------------------------------------------- people */

/**
 * Nobody was making games before this, so a start year below it is a typo rather than a career.
 * The other end is checked against the current year, which a constant cannot be.
 */
export const CAREER_START_MIN = 1970;

/**
 * The number on the card.
 *
 * Years of experience, derived from the year somebody started rather than stored as a count,
 * because a stored count is wrong every January and nobody remembers to go and fix it. The
 * first year counts: somebody who started this year is a 1 and not a 0, which is both truer of
 * how people describe themselves and the only version that does not print an empty badge.
 */
export function yearsOfExperience(
  careerStartYear: number | null | undefined,
  now: number = Date.now(),
): number | null {
  if (!careerStartYear) return null;
  const years = new Date(now).getUTCFullYear() - careerStartYear + 1;
  return years > 0 ? years : null;
}

/**
 * One person at the studio, without what they have worked on.
 *
 * This is the shape a credit carries: the game page names the people who made the game, and it
 * has no business also listing the other four games each of them has been on. The studio page
 * wants that list, and gets it from `TeamMemberProfile` in `team.ts`.
 */
export const teamMemberSchema = z.object({
  id: ulidSchema,
  slug: slugSchema,
  name: z.string(),
  /** The job, as it would be written on a contract. "Senior Level Designer". */
  title: z.string(),
  /** A few sentences in the studio's voice. Empty for somebody who has not written one yet. */
  bio: z.string(),
  /** Their portrait. Null draws the monogram, the way a game with no capsule draws its initial. */
  photoUrl: z.string().nullable(),
  /** The year they started in games. `yearsOfExperience` turns it into the number on the card. */
  careerStartYear: z.number().int().nullable(),
  location: z.string().nullable(),
  sortOrder: z.number().int(),
});
export type TeamMember = z.infer<typeof teamMemberSchema>;

/* ---------------------------------------------------------------- credits */

/** Fifty people on one game would be a different studio, and a different page. */
export const MAX_CREDITS_PER_GAME = 50;

/**
 * One person on one game, as the game page reads it.
 *
 * The roles and the line about what they did belong to this pairing and not to the person, so
 * they sit here beside the member rather than on them.
 */
export const gameCreditSchema = z.object({
  member: teamMemberSchema,
  roles: z.array(creditRoleIdSchema),
  /** What they actually did on this one. Free prose, and often the only interesting part. */
  contribution: z.string(),
  sortOrder: z.number().int(),
  updatedAt: timestampSchema,
});
export type GameCredit = z.infer<typeof gameCreditSchema>;
